import { useEffect, useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { ObjectiveDetailVM, TrainingItemAppraisalVM } from "@academy-context/read/domain/types/shared/training-item-evaluation";
import { sortObjectivesByNumber } from "@academy-context/read/domain/types/shared/training-item-evaluation";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { Form } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { LongTextField } from "@shared-kernel/primary/shared/long-text-field/long-text-field";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { ObjectiveRow } from "./objective-row.content";
import type { FormInputs } from "./form-validation/training-item-evaluation.schema";
import type { FormSubmit } from "@shared-kernel/primary/shared/api-errors/action-form";

interface Props {
  objectives: ObjectiveDetailVM[];
  appraisal: Nullable<TrainingItemAppraisalVM>;
  form: UseFormReturn<FormInputs>;
  onSubmit: FormSubmit;
  onDirtyChange?: (isDirty: boolean) => void;
}

// L'évaluation se rend d'un seul geste : tous les objectifs du groupe notés, puis l'appréciation qui
// porte sur la formation menée. Un seul formulaire, donc — le backend rejette un envoi partiel.
// Conseiller et partenaire externe rendent le même acte ; seuls l'habillage et l'endpoint diffèrent.
export const TrainingItemEvaluationForm = ({ objectives, appraisal, form, onSubmit, onDirtyChange }: Props) => {
  const sortedObjectives = useMemo(() => sortObjectivesByNumber(objectives), [objectives]);

  const {
    control,
    watch,
    setValue,
    formState: { isDirty, isSubmitting },
  } = form;

  const ratings = watch("objectives");
  const unratedCount = ratings.filter(rating => !rating.acquisitionStatus).length;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-4">
        {sortedObjectives.map((objective, index) => (
          <ObjectiveRow
            key={objective.id}
            objective={objective}
            index={index}
            selectedStatus={ratings[index]?.acquisitionStatus}
            onSelectStatus={status =>
              setValue(`objectives.${index}.acquisitionStatus`, status, { shouldDirty: true, shouldValidate: true })
            }
          />
        ))}

        <LongTextField
          control={control}
          name="disclosedAppraisal"
          rows={3}
          placeholder="Appréciation sur la formation, visible par l'élève..."
        />

        <LongTextField control={control} name="privateAppraisal" rows={3} placeholder="Appréciation privée (non visible par l'élève)..." />

        <Button type="submit" disabled={isSubmitting || unratedCount > 0} className="w-full">
          {isSubmitting
            ? "Enregistrement..."
            : unratedCount > 0
              ? "Évaluez chaque objectif"
              : appraisal
                ? "Mettre à jour l'évaluation"
                : "Enregistrer l'évaluation"}
        </Button>
      </form>
    </Form>
  );
};
